import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Title,
  Text,
  Table,
  TableHead,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Badge,
  Button,
  TextInput,
  Select,
  SelectItem,
} from '@tremor/react';
import Header from '../components/layout/Header';
import { usersApi, AgentUser, UserRole } from '../services/api';

export default function Users() {
  const [users, setUsers] = useState<AgentUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<UserRole>('viewer');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState('');

  const load = useCallback(() => {
    usersApi
      .list()
      .then((res) => setUsers(res.data.users))
      .catch((err) => setError(err.response?.data?.detail || err.message || 'Failed to load users'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviteError('');
    setInviting(true);
    try {
      await usersApi.invite(inviteEmail, inviteRole);
      setInviteEmail('');
      setInviteRole('viewer');
      load();
    } catch (err: any) {
      setInviteError(err.response?.data?.detail || err.message || 'Failed to invite user');
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (username: string, role: UserRole) => {
    await usersApi.setRole(username, role);
    load();
  };

  const handleDelete = async (username: string) => {
    if (!window.confirm(`Delete user ${username}? This cannot be undone.`)) return;
    await usersApi.remove(username);
    load();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="mx-auto max-w-6xl p-6 space-y-6">
        <Card>
          <Title>Invite User</Title>
          <form onSubmit={handleInvite} className="mt-4 flex items-end gap-3">
            <div className="flex-1">
              <Text>Email</Text>
              <TextInput
                type="email"
                placeholder="name@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                required
              />
            </div>
            <div className="w-40">
              <Text>Role</Text>
              <Select value={inviteRole} onValueChange={(v) => setInviteRole(v as UserRole)}>
                <SelectItem value="viewer">viewer</SelectItem>
                <SelectItem value="admin">admin</SelectItem>
              </Select>
            </div>
            <Button type="submit" loading={inviting}>
              Invite
            </Button>
          </form>
          {inviteError && <Text className="mt-2 text-red-500">{inviteError}</Text>}
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <Title>Users</Title>
            {!loading && !error && <Text>{users.length} users</Text>}
          </div>

          {loading && <Text className="mt-6">Loading…</Text>}
          {error && <Text className="mt-6 text-red-500">{error}</Text>}

          {!loading && !error && (
            <Table className="mt-6">
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Email</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Role</TableHeaderCell>
                  <TableHeaderCell className="text-right">Actions</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.username}>
                    <TableCell>{u.email || u.username}</TableCell>
                    <TableCell>
                      <Badge color={u.enabled ? 'green' : 'gray'}>{u.status}</Badge>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={u.role}
                        onValueChange={(v) => handleRoleChange(u.username, v as UserRole)}
                      >
                        <SelectItem value="viewer">viewer</SelectItem>
                        <SelectItem value="admin">admin</SelectItem>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="secondary" color="red" size="xs" onClick={() => handleDelete(u.username)}>
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </main>
    </div>
  );
}
